import { motion } from "framer-motion";
import { Star, BadgeCheck } from "lucide-react";
import { SITE } from "@/lib/site";

const REVIEWS = [
  { name: "Sarah M.", meta: "Local Guide · 31 reviews", stars: 5, text: "The Texas fajitas came out still sizzling and the whole table went quiet. Best Mexican food this side of the river, hands down." },
  { name: "Deacon R.", meta: "Local Guide · 12 reviews", stars: 5, text: "Family owned and it shows. They remembered our kids' orders on our second visit. The house margarita is dangerously good." },
  { name: "Priya K.", meta: "8 reviews", stars: 4, text: "Burrito Picoso lives up to the hype — big, messy, perfect heat. Gets busy on Fridays, so book ahead or go early." },
  { name: "Tom & Lisa H.", meta: "Local Guide · 47 reviews", stars: 5, text: "We've been coming since they opened. The mole enchiladas taste like someone's grandmother is in that kitchen. Because she is." },
  { name: "Marcus W.", meta: "19 reviews", stars: 5, text: "Ordered takeout for the office — twelve lunches, all correct, all hot. The carnitas tacos disappeared in minutes." },
  { name: "Elena G.", meta: "Local Guide · 63 reviews", stars: 5, text: "As someone who grew up on real Mexican cooking, I don't say this lightly: the tortillas and salsas here are the real thing." },
  { name: "Brandon C.", meta: "6 reviews", stars: 4, text: "Great value for the portion sizes. The churros with chocolate sauce are non-negotiable — order two, trust me." },
  { name: "Amy D.", meta: "Local Guide · 28 reviews", stars: 5, text: "Celebrated my dad's 70th here and the staff treated us like their own family. Beautiful room, beautiful food, beautiful people." },
];

const Stars = ({ n }) => (
  <span className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 ${i < n ? "fill-amber-warm text-amber-warm" : "text-cream/20"}`}
      />
    ))}
  </span>
);

const Card = ({ r }) => (
  <div className="w-[340px] sm:w-[400px] shrink-0 bg-clay-800 border border-terracotta/10 rounded-2xl p-6 hover:border-terracotta/35 transition-colors duration-500">
    <div className="flex items-center justify-between mb-4">
      <Stars n={r.stars} />
      <span className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-cream/30">Google</span>
    </div>
    <p className="text-sm text-cream/70 font-light leading-relaxed mb-5">"{r.text}"</p>
    <div className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-full bg-terracotta/20 border border-terracotta/30 flex items-center justify-center font-serif-d text-terracotta font-semibold">
        {r.name[0]}
      </span>
      <div>
        <p className="text-sm font-medium text-cream flex items-center gap-1.5">
          {r.name} <BadgeCheck className="w-3.5 h-3.5 text-agave" />
        </p>
        <p className="font-mono-d text-[10px] uppercase tracking-[0.15em] text-cream/35">{r.meta}</p>
      </div>
    </div>
  </div>
);

export default function Reviews() {
  const rowA = REVIEWS.slice(0, 4);
  const rowB = REVIEWS.slice(4);
  return (
    <section id="reviews" className="relative py-28 sm:py-36 overflow-hidden" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-10"
        >
          <div>
            <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-terracotta mb-6" data-testid="reviews-overline">
              Guest Love
            </p>
            <h2 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-cream leading-[1.05]" data-testid="reviews-title">
              Aurora keeps <span className="italic text-terracotta">coming back</span>
            </h2>
          </div>
          <div className="flex items-center gap-6" data-testid="reviews-summary">
            <span className="font-serif-d text-7xl font-semibold text-cream leading-none">{SITE.rating}</span>
            <div>
              <Stars n={5} />
              <p className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-cream/45 mt-2">
                {SITE.reviews} Google reviews
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1 }}
        className="space-y-5"
        data-testid="reviews-marquee"
      >
        <div className="overflow-hidden">
          <div className="marquee-track flex w-max gap-5 pl-5">
            {[0, 1].map((half) => (
              <div key={half} className="flex gap-5" aria-hidden={half === 1}>
                {[...rowA, ...rowA].map((r, i) => <Card key={`a-${half}-${i}`} r={r} />)}
              </div>
            ))}
          </div>
        </div>
        <div className="overflow-hidden">
          <div className="marquee-track-reverse flex w-max gap-5 pl-5">
            {[0, 1].map((half) => (
              <div key={half} className="flex gap-5" aria-hidden={half === 1}>
                {[...rowB, ...rowB].map((r, i) => <Card key={`b-${half}-${i}`} r={r} />)}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
